import { LOCAL_STORAGE_KEY } from '@core/configs';
import { CommonHelper } from '@core/services/common';

export interface CheckSheetSettingStorageOptions {
  data: any;
  screenStorageKey: string;
  headersStorageKey: string;
  sortConfigStorageKey: string;
  localStorageKey: string;
  isFunctionalTrainingSettingDialog: boolean;
  isShortTimeSettingDialog: boolean;
}

export function prepareCheckSheetSettingForStorage(options: CheckSheetSettingStorageOptions): any {
  if (isDefaultCheckSheetStorage(options)) return options.data;

  const { headers, sortConfig, nyuShoDisplayConfig, ...storageData } = options.data;

  // Add new check-sheet screen storage rules here.
  if (options.isFunctionalTrainingSettingDialog) {
    return prepareFunctionalTrainingSettingForStorage(options, storageData, headers, sortConfig, nyuShoDisplayConfig);
  }

  if (options.isShortTimeSettingDialog) {
    return prepareShortTimeSettingForStorage(options, storageData, headers, sortConfig);
  }

  return prepareNormalSettingForStorage(options, storageData, headers, sortConfig, nyuShoDisplayConfig);
}

function isDefaultCheckSheetStorage(options: CheckSheetSettingStorageOptions): boolean {
  return !options.screenStorageKey
    && options.headersStorageKey === LOCAL_STORAGE_KEY.settingDialogHeaders
    && options.sortConfigStorageKey === LOCAL_STORAGE_KEY.settingDialogSortConfig;
}

function prepareFunctionalTrainingSettingForStorage(
  options: CheckSheetSettingStorageOptions,
  storageData: any,
  headers: any[],
  sortConfig: any,
  nyuShoDisplayConfig: any
): any {
  const dataForStorage = updateCommonNyuShoDisplayConfig(options.localStorageKey, storageData, nyuShoDisplayConfig);
  return prepareScreenSettingForStorage(
    options,
    dataForStorage,
    headers,
    getFunctionalTrainingSortConfig(sortConfig)
  );
}

function prepareShortTimeSettingForStorage(
  options: CheckSheetSettingStorageOptions,
  storageData: any,
  headers: any[],
  sortConfig: any
): any {
  const dataForStorage = updateCommonShortTimeSortConfig(options.localStorageKey, storageData, sortConfig);
  return prepareScreenSettingForStorage(options, dataForStorage, headers, getShortTimeSortConfig(sortConfig));
}

function prepareNormalSettingForStorage(
  options: CheckSheetSettingStorageOptions,
  storageData: any,
  headers: any[],
  sortConfig: any,
  nyuShoDisplayConfig: any
): any {
  const dataForStorage = {
    ...storageData,
    ...(nyuShoDisplayConfig !== undefined ? { nyuShoDisplayConfig } : {}),
  };
  return prepareScreenSettingForStorage(options, dataForStorage, headers, sortConfig);
}

function prepareScreenSettingForStorage(
  options: CheckSheetSettingStorageOptions,
  data: any,
  headers: any[],
  sortConfig: any
): any {
  if (!options.screenStorageKey) {
    return {
      ...data,
      [options.headersStorageKey]: headers,
      [options.sortConfigStorageKey]: sortConfig,
    };
  }

  const {
    [options.screenStorageKey]: screenSetting,
    ...storageData
  } = data;

  return {
    ...storageData,
    [options.screenStorageKey]: {
      ...(screenSetting ?? {}),
      headers,
      sortConfig,
    },
  };
}

function updateCommonNyuShoDisplayConfig(localStorageKey: string, storageData: any, nyuShoDisplayConfig: any): any {
  if (nyuShoDisplayConfig === undefined) return storageData;

  return {
    ...storageData,
    nyuShoDisplayConfig: {
      ...(getStoredSettingConfig(localStorageKey)?.nyuShoDisplayConfig ?? {}),
      ...getCommonNyuShoDisplayConfig(nyuShoDisplayConfig),
    },
  };
}

function updateCommonShortTimeSortConfig(localStorageKey: string, storageData: any, sortConfig: any): any {
  return {
    ...storageData,
    sortConfig: {
      ...(getStoredSettingConfig(localStorageKey)?.sortConfig ?? {}),
      ...getCommonSortConfig(sortConfig),
    },
  };
}

function getStoredSettingConfig(localStorageKey: string): any {
  return CommonHelper.getScreenConfigByOfficeCd(localStorageKey) ?? {};
}

function getFunctionalTrainingSortConfig(sortConfig: any): any {
  return {
    isSortByRoom: sortConfig?.isSortByRoom,
    isSortByName: sortConfig?.isSortByName,
  };
}

function getCommonNyuShoDisplayConfig(nyuShoDisplayConfig: any): any {
  return {
    ...(nyuShoDisplayConfig?.timeRangeSelected !== undefined ? { timeRangeSelected: nyuShoDisplayConfig.timeRangeSelected } : {}),
    ...(nyuShoDisplayConfig?.marginToTimeSelected !== undefined ? { marginToTimeSelected: nyuShoDisplayConfig.marginToTimeSelected } : {}),
    ...(nyuShoDisplayConfig?.unitSelected !== undefined ? { unitSelected: nyuShoDisplayConfig.unitSelected } : {}),
    ...(nyuShoDisplayConfig?.groupSelected !== undefined ? { groupSelected: nyuShoDisplayConfig.groupSelected } : {}),
  };
}

function getShortTimeSortConfig(sortConfig: any): any {
  return {
    display_order: sortConfig?.display_order,
    time_grouping_flg: sortConfig?.time_grouping_flg,
    sorting_time: sortConfig?.sorting_time,
  };
}

function getCommonSortConfig(sortConfig: any): any {
  return {
    unitSelected: sortConfig?.unitSelected ?? [],
    groupSelected: sortConfig?.groupSelected ?? [],
    hidden_overtime_flg: sortConfig?.hidden_overtime_flg ?? false,
    hidden_leave_user: sortConfig?.hidden_leave_user ?? false,
  };
}
